import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  TrendingUp, Plus, Users, Phone, DollarSign, Loader2, X,
  CheckCircle, Calendar, BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { showToast } from '../../utils/toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPerformanceSection({ clientId, onUpdate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('leads'); // leads, calls, ads
  const [saving, setSaving] = useState(false);

  // Form states
  const [leadsForm, setLeadsForm] = useState({
    date: new Date().toISOString().split('T')[0],
    source: 'Google Ads',
    total_leads: '',
    qualified_leads: '',
    booked_appointments: '',
    closed_deals: '',
    notes: ''
  });

  const [callsForm, setCallsForm] = useState({
    date: new Date().toISOString().split('T')[0],
    total_calls: '',
    answered_calls: '',
    missed_calls: '',
    avg_call_duration_seconds: '',
    notes: ''
  });

  const [adsForm, setAdsForm] = useState({
    platform: 'Google',
    campaign_name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    impressions: '',
    clicks: '',
    cost: '',
    conversions: '',
    notes: ''
  });

  useEffect(() => {
    if (clientId) {
      fetchPerformanceData();
    }
  }, [clientId]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/admin/performance/summary/${clientId}?days=30`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeads = async () => {
    if (!leadsForm.total_leads) {
      showToast({ title: 'Missing data', message: 'Please enter total leads', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/performance/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          ...leadsForm,
          total_leads: parseInt(leadsForm.total_leads),
          qualified_leads: parseInt(leadsForm.qualified_leads) || 0,
          booked_appointments: parseInt(leadsForm.booked_appointments) || 0,
          closed_deals: parseInt(leadsForm.closed_deals) || 0
        })
      });

      if (response.ok) {
        showToast({ title: 'Success', message: 'Leads data added', type: 'success' });
        setShowAddModal(false);
        resetForms();
        await fetchPerformanceData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      showToast({ title: 'Error', message: 'Failed to add leads data', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddCalls = async () => {
    if (!callsForm.total_calls) {
      showToast({ title: 'Missing data', message: 'Please enter total calls', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/performance/calls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          ...callsForm,
          total_calls: parseInt(callsForm.total_calls),
          answered_calls: parseInt(callsForm.answered_calls) || 0,
          missed_calls: parseInt(callsForm.missed_calls) || 0,
          avg_call_duration_seconds: callsForm.avg_call_duration_seconds ? parseInt(callsForm.avg_call_duration_seconds) : null
        })
      });

      if (response.ok) {
        showToast({ title: 'Success', message: 'Call data added', type: 'success' });
        setShowAddModal(false);
        resetForms();
        await fetchPerformanceData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      showToast({ title: 'Error', message: 'Failed to add call data', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddAds = async () => {
    if (!adsForm.cost) {
      showToast({ title: 'Missing data', message: 'Please enter ad spend', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/performance/ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          ...adsForm,
          impressions: parseInt(adsForm.impressions) || 0,
          clicks: parseInt(adsForm.clicks) || 0,
          cost: parseFloat(adsForm.cost),
          conversions: parseInt(adsForm.conversions) || 0
        })
      });

      if (response.ok) {
        showToast({ title: 'Success', message: 'Ad data added', type: 'success' });
        setShowAddModal(false);
        resetForms();
        await fetchPerformanceData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      showToast({ title: 'Error', message: 'Failed to add ad data', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resetForms = () => {
    setLeadsForm({
      date: new Date().toISOString().split('T')[0],
      source: 'Google Ads',
      total_leads: '',
      qualified_leads: '',
      booked_appointments: '',
      closed_deals: '',
      notes: ''
    });
    setCallsForm({
      date: new Date().toISOString().split('T')[0],
      total_calls: '',
      answered_calls: '',
      missed_calls: '',
      avg_call_duration_seconds: '',
      notes: ''
    });
    setAdsForm({
      platform: 'Google',
      campaign_name: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      impressions: '',
      clicks: '',
      cost: '',
      conversions: '',
      notes: ''
    });
  };

  const prepareChartData = () => {
    if (!summary || !summary.leads_timeline) return [];
    
    const dataMap = {};
    summary.leads_timeline.forEach(record => {
      const date = record.date;
      if (!dataMap[date]) {
        dataMap[date] = { date, leads: 0 };
      }
      dataMap[date].leads += record.total_leads;
    });

    return Object.values(dataMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  const chartData = prepareChartData();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Performance Analytics
            </div>
            <Button size="sm" onClick={() => { setModalType('leads'); setShowAddModal(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Data
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* KPI Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-blue-600" />
                <p className="text-xs text-blue-600 font-medium">Total Leads</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">{summary?.total_leads || 0}</p>
            </div>

            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <p className="text-xs text-green-600 font-medium">Qualified</p>
              </div>
              <p className="text-2xl font-bold text-green-900">{summary?.qualified_leads || 0}</p>
            </div>

            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-purple-600" />
                <p className="text-xs text-purple-600 font-medium">Appointments</p>
              </div>
              <p className="text-2xl font-bold text-purple-900">{summary?.booked_appointments || 0}</p>
            </div>

            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <div className="flex items-center gap-2 mb-1">
                <Phone className="h-4 w-4 text-yellow-600" />
                <p className="text-xs text-yellow-600 font-medium">Total Calls</p>
              </div>
              <p className="text-2xl font-bold text-yellow-900">{summary?.total_calls || 0}</p>
            </div>
          </div>

          {/* Ad Performance Metrics */}
          {summary?.total_ad_spend > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Ad Performance (Last 30 Days)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-600 text-xs">Total Spend</p>
                  <p className="font-semibold text-gray-900">${summary.total_ad_spend.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Impressions</p>
                  <p className="font-semibold text-gray-900">{summary.total_impressions.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Clicks</p>
                  <p className="font-semibold text-gray-900">{summary.total_clicks.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Avg CPC</p>
                  <p className="font-semibold text-gray-900">
                    ${summary.avg_cpc ? summary.avg_cpc.toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Leads Chart */}
          {chartData.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Leads Trend (Last 30 Days)
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6b7280"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button size="sm" variant="outline" onClick={() => { setModalType('leads'); setShowAddModal(true); }}>
              <Plus className="h-3 w-3 mr-1" />
              Add Leads
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setModalType('calls'); setShowAddModal(true); }}>
              <Plus className="h-3 w-3 mr-1" />
              Add Calls
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setModalType('ads'); setShowAddModal(true); }}>
              <Plus className="h-3 w-3 mr-1" />
              Add Ad Data
            </Button>
          </div>

          {/* Empty State */}
          {(!summary || summary.total_leads === 0) && (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No performance data yet</p>
              <p className="text-xs mt-1">Click "Add Data" to start tracking</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Data Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add {modalType === 'leads' ? 'Leads' : modalType === 'calls' ? 'Call' : 'Ad'} Data
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Leads Form */}
            {modalType === 'leads' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" value={leadsForm.date} onChange={(e) => setLeadsForm({...leadsForm, date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Source *</Label>
                    <Select value={leadsForm.source} onValueChange={(value) => setLeadsForm({...leadsForm, source: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Google Ads">Google Ads</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Direct">Direct</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Total Leads *</Label>
                    <Input type="number" value={leadsForm.total_leads} onChange={(e) => setLeadsForm({...leadsForm, total_leads: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Qualified Leads</Label>
                    <Input type="number" value={leadsForm.qualified_leads} onChange={(e) => setLeadsForm({...leadsForm, qualified_leads: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Appointments</Label>
                    <Input type="number" value={leadsForm.booked_appointments} onChange={(e) => setLeadsForm({...leadsForm, booked_appointments: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Closed Deals</Label>
                    <Input type="number" value={leadsForm.closed_deals} onChange={(e) => setLeadsForm({...leadsForm, closed_deals: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={leadsForm.notes} onChange={(e) => setLeadsForm({...leadsForm, notes: e.target.value})} />
                </div>
                <div className="flex gap-2 pt-3">
                  <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleAddLeads} disabled={saving} className="flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Leads'}
                  </Button>
                </div>
              </div>
            )}

            {/* Calls Form */}
            {modalType === 'calls' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Date *</Label>
                  <Input type="date" value={callsForm.date} onChange={(e) => setCallsForm({...callsForm, date: e.target.value})} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Total Calls *</Label>
                    <Input type="number" value={callsForm.total_calls} onChange={(e) => setCallsForm({...callsForm, total_calls: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Answered</Label>
                    <Input type="number" value={callsForm.answered_calls} onChange={(e) => setCallsForm({...callsForm, answered_calls: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Missed</Label>
                    <Input type="number" value={callsForm.missed_calls} onChange={(e) => setCallsForm({...callsForm, missed_calls: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Avg Duration (seconds)</Label>
                  <Input type="number" value={callsForm.avg_call_duration_seconds} onChange={(e) => setCallsForm({...callsForm, avg_call_duration_seconds: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={callsForm.notes} onChange={(e) => setCallsForm({...callsForm, notes: e.target.value})} />
                </div>
                <div className="flex gap-2 pt-3">
                  <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleAddCalls} disabled={saving} className="flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Calls'}
                  </Button>
                </div>
              </div>
            )}

            {/* Ads Form */}
            {modalType === 'ads' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Platform *</Label>
                    <Select value={adsForm.platform} onValueChange={(value) => setAdsForm({...adsForm, platform: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Google">Google</SelectItem>
                        <SelectItem value="Meta">Meta/Facebook</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Campaign Name</Label>
                    <Input value={adsForm.campaign_name} onChange={(e) => setAdsForm({...adsForm, campaign_name: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" value={adsForm.start_date} onChange={(e) => setAdsForm({...adsForm, start_date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input type="date" value={adsForm.end_date} onChange={(e) => setAdsForm({...adsForm, end_date: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Impressions</Label>
                    <Input type="number" value={adsForm.impressions} onChange={(e) => setAdsForm({...adsForm, impressions: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Clicks</Label>
                    <Input type="number" value={adsForm.clicks} onChange={(e) => setAdsForm({...adsForm, clicks: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Cost ($) *</Label>
                    <Input type="number" step="0.01" value={adsForm.cost} onChange={(e) => setAdsForm({...adsForm, cost: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs">Conversions</Label>
                    <Input type="number" value={adsForm.conversions} onChange={(e) => setAdsForm({...adsForm, conversions: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={adsForm.notes} onChange={(e) => setAdsForm({...adsForm, notes: e.target.value})} />
                </div>
                <div className="flex gap-2 pt-3">
                  <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleAddAds} disabled={saving} className="flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Ad Data'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
